import {
	Button,
	ChipConfirmModal,
	EmptyState,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ondit/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { useAccess, whyDisabled } from "~/core/access/index";
import { ApiError, api, type CrawlDay, type CrawlerBot, ORIGIN } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { article as articlePath } from "~/core/paths";
import { keys, read } from "~/core/query";
import { Section, Stat } from "~/core/ui/page";
import { Async } from "~/core/ui/states";
import { Drill, DrillLink } from "~/modules/analytics/components/detail";
import { type ReadingProps, StageShell } from "~/modules/analytics/components/stage";
import { siteSetup } from "~/modules/analytics/pages/tracking/shared";

/** The order the page reads in: the fetch that is about to become a citation first. */
const PURPOSES = ["live", "search", "training", "preview"] as const;

/**
 * Who came to read the page.
 *
 * Everything else in this panel measures what an assistant said. This measures
 * whether it ever came to look — the step before all of them, and the only
 * failure in the chain that nobody notices on their own: a bot rule that
 * answers 403 to GPTBot looks like a working site to every human who checks,
 * and the citation numbers just quietly stop growing.
 *
 * The counts for this directory's own pages need no setup, because this
 * directory serves them.
 */
export function CrawlersPage({ entityId, filters, setFilters }: ReadingProps) {
	const t = useT();
	const { locale } = useI18n();
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "crawlers", filters.range],
		queryFn: () => api.crawlers(entityId, { range: filters.range }),
		...read,
	});

	return (
		<StageShell
			entityId={entityId}
			reading="crawlers"
			lede={t("crawlers.lede")}
			filters={filters}
			setFilters={setFilters}
		>
			<Async state={state}>
				{(data) => (
					<>
						<div className="stats insight-stats">
							<Stat
								label={t("crawlers.hits")}
								hint={t("hint.crawlerHits")}
								value={fmt.number(locale, data.total_hits)}
							/>
							<Stat
								label={t("crawlers.errors")}
								hint={t("hint.crawlerErrors")}
								value={fmt.number(locale, data.total_errors)}
							/>
							<Stat label={t("crawlers.vendors")} value={String(new Set(data.bots.map((b) => b.vendor)).size)} />
						</div>

						{!data.ever ? (
							<EmptyState
								size="compact"
								title={t("crawlers.emptyTitle")}
								description={t("crawlers.empty", { host: data.site_host })}
							/>
						) : (
							<>
								<Section title={t("crawlers.daily")} lede={t("crawlers.dailyHint")}>
									<Days series={data.series} />
								</Section>

								{PURPOSES.map((purpose) => {
									const rows = data.bots.filter((bot) => bot.purpose === purpose);
									if (rows.length === 0) return null;
									return (
										<Section
											key={purpose}
											title={t(`crawlers.purpose.${purpose}`)}
											lede={t(`crawlers.purposeHint.${purpose}`)}
										>
											<Bots rows={rows} />
										</Section>
									);
								})}

								<Section title={t("crawlers.pages")} lede={t("crawlers.pagesHint")}>
									<div className="table-scroll">
										<Table variant="resource">
											<TableHeader>
												<TableRow>
													<TableHead className="wrap">{t("crawlers.path")}</TableHead>
													<TableHead className="num">{t("crawlers.hits")}</TableHead>
													<TableHead className="num">{t("crawlers.errors")}</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{data.top_pages.map((page) => (
													<TableRow key={`${page.host}${page.path}`}>
														<TableCell className="wrap mono">
															{/* A path opens onto who read it, which is the only
															    way to find out whose crawler is being refused. */}
															<Drill
																view={{
																	kind: "crawler",
																	label: page.path,
																	path: page.path,
																	host: page.host,
																}}
															>
																{page.path}
															</Drill>
															<div className="faint">
																{page.host}
																{/* Where the path is one of ours, the way to what
																    it says. A fetch is only interesting next to the
																    page that was fetched. */}
																{page.page_id ? (
																	<>
																		{" · "}
																		<DrillLink to={articlePath(entityId, page.page_id)}>
																			{t("crawlers.thePage")}
																		</DrillLink>
																	</>
																) : null}
															</div>
														</TableCell>
														<TableCell className="num">{fmt.number(locale, page.hits)}</TableCell>
														{/* The only red on this screen, and it is the
														    reason the screen exists. */}
														<TableCell className={page.errors > 0 ? "num warn" : "num"}>
															{fmt.number(locale, page.errors)}
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</div>
								</Section>
							</>
						)}

						{/* The brand's own domain is counted by a Worker placed on it,
						    which is set up with everything else placed there. */}
						<p className="hint mt-(--s-8)">
							{t(data.own_site ? "crawlers.ownSiteLive" : "crawlers.ownSiteMoved")}{" "}
							<Link className="drill" to={siteSetup(entityId, "crawlers")}>
								{t("crawlers.ownSiteSetup")}
							</Link>
						</p>
					</>
				)}
			</Async>
		</StageShell>
	);
}

/**
 * A day per column, stacked by why the crawlers came.
 *
 * Not a chart library and not a line: the question is "did anything come, and
 * was it the kind that answers a question" — a shape read in one glance, on a
 * series that is at most ninety values long.
 */
function Days({ series }: { series: CrawlDay[] }) {
	const t = useT();
	const { locale } = useI18n();
	const tallest = Math.max(1, ...series.map(total));

	return (
		<div className="days">
			{series.map((day) => (
				<div className="day" key={day.date} title={`${day.date} · ${fmt.number(locale, total(day))}`}>
					<div className="day-bar" style={{ height: `${(total(day) / tallest) * 100}%` }}>
						{PURPOSES.map((purpose) =>
							day[purpose] > 0 ? (
								<span
									key={purpose}
									className={`day-part day-${purpose}`}
									style={{ height: `${(day[purpose] / total(day)) * 100}%` }}
								/>
							) : null,
						)}
					</div>
				</div>
			))}
			{/* The legend is the only place the four colours are named, so it is
			    part of the chart rather than a caption under it. */}
			<div className="day-legend">
				{PURPOSES.map((purpose) => (
					<span key={purpose}>
						<i className={`day-part day-${purpose}`} aria-hidden />
						{t(`crawlers.purpose.${purpose}`)}
					</span>
				))}
			</div>
		</div>
	);
}

function total(day: CrawlDay): number {
	return day.training + day.search + day.live + day.preview;
}

function Bots({ rows }: { rows: CrawlerBot[] }) {
	const t = useT();
	const { locale } = useI18n();
	return (
		<div className="table-scroll">
			<Table variant="resource">
				<TableHeader>
					<TableRow>
						<TableHead>{t("crawlers.bot")}</TableHead>
						<TableHead>{t("crawlers.vendor")}</TableHead>
						<TableHead className="num">{t("crawlers.hits")}</TableHead>
						<TableHead className="num">{t("crawlers.pagesRead")}</TableHead>
						<TableHead className="num">{t("crawlers.errors")}</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{rows.map((bot) => (
						<TableRow key={bot.bot}>
							<TableCell className="mono">
								<Drill view={{ kind: "crawler", label: bot.label, bot: bot.bot }}>{bot.label}</Drill>
							</TableCell>
							<TableCell>{bot.vendor}</TableCell>
							<TableCell className="num">
								<Drill view={{ kind: "crawler", label: bot.label, bot: bot.bot }}>{fmt.number(locale, bot.hits)}</Drill>
							</TableCell>
							<TableCell className="num">
								<Drill view={{ kind: "crawler", label: bot.label, bot: bot.bot }}>
									{fmt.number(locale, bot.pages)}
								</Drill>
							</TableCell>
							<TableCell className={bot.errors > 0 ? "num warn" : "num"}>{fmt.number(locale, bot.errors)}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</div>
	);
}

/**
 * Settings › Site setup's crawler reporter: the same picture as the AI crawlers
 * reading, for the brand's own domain, which needs a token and a few lines at
 * its edge.
 *
 * Optional and not a wizard: the directory's own counts are useful on the day
 * the listing goes live, and this is the half that needs the brand's site.
 */
export function CrawlerSetup({ entityId }: { entityId: string }) {
	const state = useQuery({
		queryKey: [...keys.entity(entityId), "crawlers", "30d"],
		queryFn: () => api.crawlers(entityId, { range: "30d" }),
		...read,
	});
	return <Async state={state}>{(data) => <OwnSite entityId={entityId} token={data.token} />}</Async>;
}

function OwnSite({ entityId, token }: { entityId: string; token: string | null }) {
	const t = useT();
	const { locale } = useI18n();
	const { editor, why } = useAccess();
	const [busy, setBusy] = useState(false);
	const [confirming, setConfirming] = useState(false);
	const queryClient = useQueryClient();
	const rotate = useMutation({
		mutationFn: () => api.rotateCrawlToken(entityId),
		onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.entity(entityId) }),
	});

	async function mint() {
		if (busy) return;
		setBusy(true);
		try {
			await rotate.mutateAsync();
			toast.success(t("crawlers.tokenMinted"));
		} catch (e) {
			toast.error(e instanceof ApiError ? e.localized(locale) : String(e));
		} finally {
			setBusy(false);
		}
	}

	async function copy(value: string) {
		try {
			await navigator.clipboard.writeText(value);
			toast.success(t("badge.copied"));
		} catch {
			// Refused clipboard. The snippet is selectable.
		}
	}

	return (
		<Section
			title={t("crawlers.ownSite")}
			lede={t("crawlers.ownSiteHint")}
			action={
				// The first token is minted on the click. A replacement is asked
				// about first: the one in the deployed snippet dies with it.
				<Button
					type="button"
					disabled={busy || !editor}
					title={whyDisabled(editor, why.editor)}
					onClick={() => (token ? setConfirming(true) : mint())}
				>
					{token ? t("crawlers.rotate") : t("crawlers.connect")}
				</Button>
			}
		>
			<ChipConfirmModal
				open={confirming}
				onOpenChange={setConfirming}
				title={t("crawlers.rotateTitle")}
				text={t("crawlers.rotateBody")}
				dismissLabel={t("common.cancel")}
				confirm={{ label: t("crawlers.rotate"), variant: "destructive", onClick: mint }}
			/>
			{token ? (
				<>
					<pre className="mono">{worker(token)}</pre>
					<Button variant="default" type="button" onClick={() => copy(worker(token))}>
						{t("badge.copy")}
					</Button>
				</>
			) : (
				<p className="hint">{t("crawlers.ownSiteOff")}</p>
			)}
		</Section>
	);
}

/**
 * The reporter, as a Cloudflare Worker.
 *
 * It sends the user-agent rather than deciding what it is: the table that says
 * which crawler an agent is, and why it came, lives on the server, so a worker
 * installed today counts a crawler added next year without being touched.
 */
function worker(token: string): string {
	return `export default {
  async fetch(request, env, ctx) {
    const response = await fetch(request);
    const ua = request.headers.get("user-agent") || "";
    if (/bot|crawler|spider|-user|gpt|claude|perplexity/i.test(ua)) {
      const url = new URL(request.url);
      ctx.waitUntil(
        fetch("${ORIGIN || window.location.origin}/api/v1/ingest/crawl", {
          method: "POST",
          headers: { "content-type": "application/json", "x-ondit-crawl-token": "${token}" },
          body: JSON.stringify({
            hits: [{
              day: new Date().toISOString().slice(0, 10),
              host: url.host,
              path: url.pathname,
              user_agent: ua,
              hits: 1,
              errors: response.status >= 400 ? 1 : 0,
            }],
          }),
        }).catch(() => {}),
      );
    }
    return response;
  },
};`;
}
